import { recipeApp, type OsRecipe } from '../os-recipes.js';

const FIGMA = recipeApp('com.figma.Desktop');

/** figma: its app recipes as data (Mac recipes R3). Every entry ships held until its drill lifts it.
 *  Figma is Electron with a WebGL canvas: chords, its menu bar and deep links only. Its tools and
 *  panels are bare keys, so each runs behind the focus guard (a letter in a text layer is a live edit).
 *  The research rows not here are on the R3 review lists. */
export const RECIPES: readonly OsRecipe[] = [
  {
    id: 'figma.new_design_file', app: FIGMA, status: 'held', opens: true,
    title: 'New Figma design file',
    description: 'New Figma design file ("new figma file").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+n' }],
    tier: 'reversible', cues: ['nayi', 'banao', 'blank', 'new file', 'new figma file', 'new design', 'new design file', 'new figma design', 'blank figma file', 'nayi file banao', 'file banao'],
    reply: 'Started a new design file in Figma.',
  },
  {
    id: 'figma.new_figjam_file', app: FIGMA, status: 'held', opens: true,
    title: 'New FigJam board',
    description: 'New FigJam board ("new figjam board").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+opt+n' }],
    tier: 'reversible', cues: ['nayi', 'banao', 'blank', 'new figjam', 'new figjam board', 'create a figjam', 'new whiteboard', 'start a whiteboard', 'new board', 'new brainstorm board', 'blank figjam', 'nayi figjam banao'],
    reply: 'Started a new FigJam board.',
  },
  {
    id: 'figma.new_slides_file', app: FIGMA, status: 'held', opens: true,
    title: 'New Figma Slides deck',
    description: 'New Figma Slides deck ("new figma slides deck").',
    params: {},
    steps: [{ op: 'open_url', template: 'figma://slides/new' }, { op: 'await_front', timeoutMs: 3000 }],
    tier: 'reversible', cues: ['naya', 'banao', 'blank', 'new slides', 'new figma slides', 'new slides file', 'new figma slides deck', 'new deck', 'create a presentation', 'naya deck banao', 'blank figma slides'],
    reply: 'Started a new Slides deck in Figma.',
  },
  {
    id: 'figma.file_browser', app: FIGMA, status: 'held', opens: true,
    title: "Open Figma's file browser",
    description: 'Open Figma\'s file browser ("show my figma files").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+o' }],
    tier: 'reversible', cues: ['files', 'browser', 'home', 'recent', 'drafts', 'file browser', 'go to figma home', 'files dikhao'],
    reply: 'Opened your files in Figma.',
  },
  {
    id: 'figma.open_copied_link', app: FIGMA, status: 'held', opens: true,
    title: 'Open the Figma link on the clipboard',
    description: 'Open the Figma link on the clipboard ("open the figma link I copied").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['File', 'Open File URL From Clipboard'] }],
    tier: 'reversible', cues: ['copied', 'clipboard', 'link', 'link i', 'that copied figma url', 'design link i'],
    reply: 'Opened the copied link in Figma.',
  },
  {
    id: 'figma.switch_tab', app: FIGMA, status: 'held', opens: true,
    title: 'Switch to an open Figma tab by file name',
    description: 'Switch to an open Figma tab by file name ("switch to the homepage tab in figma").',
    params: { title: { kind: 'span', description: "Words from the file's name" } },
    steps: [{ op: 'activate' }, { op: 'cycle_to_title', param: 'title', chord: 'ctrl+tab', max: 12 }],
    tier: 'reversible', verify: 'title', cues: ['tab', 'switch', 'file tab'],
    reply: 'Switched to {label} in Figma.',
  },
  {
    id: 'figma.new_tab', app: FIGMA, status: 'held', opens: true,
    title: 'Open a new Figma tab',
    description: 'Open a new Figma tab ("new tab in figma").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+t' }],
    tier: 'reversible', cues: ['naya', 'another', 'fresh', 'new tab', 'new figma tab', 'another tab', 'naya tab', 'fresh figma tab'],
    reply: 'Opened a new tab in Figma.',
  },
  // A close is a destructive menu word to the lint, so it waits for the yes (Reopen Closed Tab brings it back).
  {
    id: 'figma.close_tab', app: FIGMA, status: 'held',
    title: 'Close the Figma tab',
    description: 'Close the Figma tab ("close this figma tab").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'menu', path: ['File', 'Close Tab'] }],
    tier: 'irreversible', cues: ['close this figma tab', 'close the tab', 'close this file tab', 'shut this tab', 'tab band', 'close tab'],
    reply: 'Close this tab in Figma?',
  },
  {
    id: 'figma.reopen_tab', app: FIGMA, status: 'held', opens: true,
    title: 'Reopen the last closed Figma tab',
    description: 'Reopen the last closed Figma tab ("reopen the tab I closed in figma").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+shift+t' }],
    tier: 'reversible', cues: ['reopen', 'restore', 'closed', 'wapas', 'tab i closed', 'back the closed file', 'restore that figma tab', 'closed tab', 'woh tab wapas', 'oops reopen it'],
    reply: 'Reopened the last closed tab in Figma.',
  },
  {
    id: 'figma.next_tab', app: FIGMA, status: 'held', opens: true,
    title: 'Next Figma tab',
    description: 'Next Figma tab ("next tab in figma").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'ctrl+tab' }],
    tier: 'reversible', cues: ['next', 'right', 'agla', 'next tab', 'next file tab', 'tab on the right', 'next figma tab', 'agla tab'],
    reply: 'Next tab.',
  },
  {
    id: 'figma.previous_tab', app: FIGMA, status: 'held', opens: true,
    title: 'Previous Figma tab',
    description: 'Previous Figma tab ("previous tab in figma").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'ctrl+shift+tab' }],
    tier: 'reversible', cues: ['previous', 'prev', 'left', 'pichla', 'previous tab', 'tab on the left', 'previous file tab', 'pichla tab', 'prev tab'],
    reply: 'Previous tab.',
  },
  {
    id: 'figma.new_window', app: FIGMA, status: 'held', opens: true,
    title: 'Open a new Figma window',
    description: 'Open a new Figma window ("new figma window").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+shift+n' }],
    tier: 'reversible', cues: ['nayi', 'another', 'second', 'new window', 'new figma window', 'another window', 'another figma window', 'second window', 'nayi window'],
    reply: 'Opened a new Figma window.',
  },
  // drill: Electron focus role
  {
    id: 'figma.zoom_fit', app: FIGMA, status: 'held',
    title: 'Zoom Figma to fit everything',
    description: 'Zoom Figma to fit everything ("zoom to fit").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'shift+1' }],
    tier: 'reversible', cues: ['fit', 'whole', 'all', 'fit everything on screen', 'whole canvas', 'fit to screen'],
    reply: 'Zoomed to fit.',
  },
  // drill: Electron focus role
  {
    id: 'figma.zoom_selection', app: FIGMA, status: 'held',
    title: 'Zoom Figma to the selection',
    description: 'Zoom Figma to the selection ("zoom to selection").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'shift+2' }],
    tier: 'reversible', cues: ['selection', 'selected', 'frame', 'focus on the selected', 'selection up close'],
    reply: 'Zoomed to the selection.',
  },
  // drill: Electron focus role
  {
    id: 'figma.zoom_100', app: FIGMA, status: 'held',
    title: 'Zoom Figma to 100 percent',
    description: 'Zoom Figma to 100 percent ("zoom to 100 percent").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'shift+0' }],
    tier: 'reversible', cues: ['percent', 'hundred', 'actual', 'reset', 'real size', 'actual size'],
    reply: 'Zoomed to 100 percent.',
  },
  {
    id: 'figma.zoom_in', app: FIGMA, status: 'held',
    title: 'Zoom in on the Figma canvas',
    description: 'Zoom in on the Figma canvas ("zoom in").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+=' }],
    tier: 'reversible', cues: ['zoom', 'closer', 'zoom in on', 'zoom in a bit', 'thoda zoom in karo', 'zoom in more'],
    reply: 'Zoomed in.',
  },
  {
    id: 'figma.zoom_out', app: FIGMA, status: 'held',
    title: 'Zoom out on the Figma canvas',
    description: 'Zoom out on the Figma canvas ("zoom out").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+-' }],
    tier: 'reversible', cues: ['zoom', 'further', 'zoom out on', 'further away', 'thoda zoom out karo', 'zoom out more'],
    reply: 'Zoomed out.',
  },
  // drill: Electron focus role
  {
    id: 'figma.next_page', app: FIGMA, status: 'held', opens: true,
    title: 'Next page in the Figma file',
    description: 'Next page in the Figma file ("next page in figma").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'pagedown' }],
    tier: 'reversible', cues: ['next', 'following', 'agla', 'next page', 'agla page', 'next figma page'],
    reply: 'Next page.',
  },
  // drill: Electron focus role
  {
    id: 'figma.previous_page', app: FIGMA, status: 'held', opens: true,
    title: 'Previous page in the Figma file',
    description: 'Previous page in the Figma file ("previous page in figma").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'pageup' }],
    tier: 'reversible', cues: ['previous', 'before', 'pichla', 'previous page', 'pichla page'],
    reply: 'Previous page.',
  },
  // drill: Electron focus role
  {
    id: 'figma.layers_panel', app: FIGMA, status: 'held', opens: true,
    title: "Show Figma's layers panel",
    description: 'Show Figma\'s layers panel ("show layers").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'opt+1' }],
    tier: 'reversible', cues: ['layers', 'layer', 'layers panel', 'layers dikhao'],
    reply: 'Showing layers.',
  },
  // drill: Electron focus role
  {
    id: 'figma.assets_panel', app: FIGMA, status: 'held', opens: true,
    title: "Show Figma's assets panel",
    description: 'Show Figma\'s assets panel ("show assets").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'opt+2' }],
    tier: 'reversible', cues: ['assets', 'components', 'library', 'assets panel', 'component library', 'assets dikhao'],
    reply: 'Showing assets.',
  },
  {
    id: 'figma.toggle_ui', app: FIGMA, status: 'held',
    title: "Toggle Figma's panels",
    description: 'Show or hide Figma\'s panels ("hide the ui").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+\\' }],
    tier: 'reversible', cues: ['ui', 'panels', 'interface', 'canvas', 'hide the ui', 'hide all the panels', 'panels again', 'clean canvas', 'panels hatao', 'toggle the figma interface'],
    reply: "Toggled Figma's panels.",
  },
  // drill: Electron focus role
  {
    id: 'figma.rulers', app: FIGMA, status: 'held',
    title: 'Toggle Figma rulers',
    description: 'Show or hide Figma rulers ("show rulers").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'shift+r' }],
    tier: 'reversible', cues: ['rulers', 'ruler', 'hide the rulers', 'turn on rulers', 'toggle rulers', 'rulers dikhao', 'i need the rulers'],
    reply: 'Toggled rulers.',
  },
  {
    id: 'figma.outlines', app: FIGMA, status: 'held',
    title: 'Toggle Figma outline mode',
    description: 'Toggle Figma outline mode ("outline mode").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+y' }],
    tier: 'reversible', cues: ['outline', 'outlines', 'wireframe', 'outline mode', 'wireframe view', 'turn off outlines', 'outlines dikhao', 'toggle outline view'],
    reply: 'Toggled outline mode.',
  },
  {
    id: 'figma.layout_guides', app: FIGMA, status: 'held',
    title: 'Toggle Figma layout guides',
    description: 'Toggle Figma layout guides ("show layout grids").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'ctrl+g' }],
    tier: 'reversible', cues: ['grid', 'grids', 'guides', 'columns', 'layout grids', 'hide the layout guides', 'toggle the grid', 'turn on layout grids', 'grid dikhao'],
    reply: 'Toggled layout guides.',
  },
  // drill: Electron focus role
  {
    id: 'figma.dev_mode', app: FIGMA, status: 'held',
    title: 'Toggle Figma Dev Mode',
    description: 'Toggle Figma Dev Mode ("switch to dev mode").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'shift+d' }],
    tier: 'reversible', cues: ['dev mode', 'developer', 'handoff', 'turn off dev mode', 'handoff view', 'toggle dev mode'],
    reply: 'Toggled Dev Mode.',
  },
  // The catalog audit leaves exports for the yes; the yes only opens the dialog.
  {
    id: 'figma.export', app: FIGMA, status: 'held',
    title: "Open Figma's export dialog",
    description: 'Open Figma\'s export dialog ("export this").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'key', chord: 'cmd+shift+e' }],
    tier: 'irreversible', cues: ['export', 'export this', 'export the selection', 'export as png'],
    reply: 'Open the export dialog in Figma?',
  },
  {
    id: 'figma.copy_as_png', app: FIGMA, status: 'held',
    title: 'Copy the Figma selection as a PNG',
    description: 'Copy the Figma selection as a PNG ("copy this as png").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+shift+c' }],
    tier: 'reversible', cues: ['png', 'image', 'picture', 'copy this as png', 'copy as png'],
    reply: 'Copied the selection as an image.',
  },
  {
    id: 'figma.version_history', app: FIGMA, status: 'held', opens: true,
    title: 'Show Figma version history',
    description: 'Show Figma version history ("show version history").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['File', 'Show Version History'] }],
    tier: 'reversible', cues: ['version', 'versions', 'history', 'changed', 'file history', 'see older versions', 'history dikhao', 'history of this file'],
    reply: 'Showing version history.',
  },
  // A saved version is visible to collaborators: the catalog audit leaves saves for the yes.
  {
    id: 'figma.save_version', app: FIGMA, status: 'held',
    title: 'Start saving a named Figma version',
    description: 'Start saving a named Figma version ("save a version").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'menu', path: ['File', 'Save to Version History'] }],
    tier: 'irreversible', cues: ['version', 'checkpoint', 'save a version', 'save to version history', 'checkpoint this file', 'version save'],
    reply: 'Save a named version of this file in Figma?',
  },
  // Presenting shows the file to the room, and a Return chord waits for the yes; drill the chord. R4b: cued
  // by a play word, never the prototype alone ("open the prototype" is never a presentation).
  {
    id: 'figma.present', app: FIGMA, status: 'held',
    title: 'Present the Figma prototype',
    description: 'Present the Figma prototype ("present this").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'key', chord: 'cmd+opt+return' }],
    tier: 'irreversible', cues: ['present', 'presenting', 'play', 'present this', 'play the prototype', 'present the prototype', 'start the prototype', 'prototype chalao'],
    reply: 'Present this prototype in Figma?',
  },
  {
    id: 'figma.shortcuts_panel', app: FIGMA, status: 'held', opens: true,
    title: "Open Figma's keyboard shortcuts panel",
    description: 'Open Figma\'s keyboard shortcuts panel ("show figma shortcuts").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'ctrl+shift+/' }],
    tier: 'reversible', cues: ['shortcuts', 'hotkeys', 'shortcut', 'show figma shortcuts', 'shortcuts dikhao', 'keyboard shortcuts in', 'help with figma shortcuts'],
    reply: "Opened Figma's shortcuts panel.",
  },
];
